import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetResourcesResponse } from '@/services/api/api.types'

export function useStepResources(stepId: string | undefined | null, contentSourceUrl?: string | null) {
  const { api } = useGlobalState()

  const stepResourcesQuery = useQuery({
    queryKey: ['step', 'resources', 'all', stepId],
    queryFn: async () => {
      return api.request<GetResourcesResponse>('/cm/get_resources', 'get', {
        step_id: stepId!,
      })
    },
    enabled: !!stepId,
  })

  const stepResources = stepResourcesQuery.data

  const uploadedFileResource = stepResources?.find(
    r => r.isSourceFile && r.type !== 'text' && r.type !== 'url' && r.type !== 'youtube',
  )

  const sourceFileResource = useMemo(() => {
    const resource = stepResources?.find(r => r.isSourceFile)
    if (!resource) return undefined

    // Backfill from /content/get sourceUrl for entries where the resource fields are blank
    if (!resource.data && !resource.sourceUrl && contentSourceUrl) {
      return { ...resource, sourceUrl: contentSourceUrl }
    }

    return resource
  }, [stepResources, contentSourceUrl])

  // `data` stays undefined after a failed fetch, so callers that wait on resources need
  // this to tell "loading" from "failed" and not park forever.
  const stepResourcesSettled = stepResourcesQuery.isSuccess || stepResourcesQuery.isError

  return { stepResources, stepResourcesSettled, uploadedFileResource, sourceFileResource }
}
